import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircleOutlined, CopyOutlined, EditOutlined, HistoryOutlined, PlusOutlined, ReloadOutlined, StopOutlined, SwapOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Form, Input, InputNumber, Modal, Drawer, Row, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import moment from 'moment';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import { changeCommissionPolicyStatus, cloneCommissionPolicy, compareCommissionPolicyVersions, createCommissionPolicyVersion, fetchCommissionPolicies, fetchCommissionPolicyDetail, saveCommissionPolicy, validateCommissionPolicyVersion } from '@/redux/actions/commissionPolicyActions';
import { isMobile } from 'react-device-detect';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { cleanObject } from '@/utils/helpers';
import '@/assets/css/CommissionPolicyPage.css';
import { PolicyAuditHistory, PolicyVersionContent } from './CommissionPolicyDetailContent';
import usePolicyFormSave from './usePolicyFormSave';
import { showCommissionBusinessError } from '@/utils/commission/errorPresenter';

const required = label => ({ required: true, message: `Vui lòng nhập ${label}.` });
const latestVersion = policy => (policy.versions || [])[0] || {};
const beneficiaryRole = policy => latestVersion(policy).role_key || policy.role_key;
const roleLabel = (policy, capabilities) => (capabilities.catalogs?.role_keys || []).find(item => item.value === beneficiaryRole(policy))?.label || 'Chưa xác định vai trò';
const statusLabel = status => ({ DRAFT: 'Bản nháp', SCHEDULED: 'Đã lên lịch', ACTIVE: 'Đang áp dụng', RETIRED: 'Đã ngừng' }[status] || status);
const statusColor = status => ({ DRAFT: 'default', SCHEDULED: 'gold', ACTIVE: 'green', RETIRED: 'red' }[status] || 'default');
const formatDate = value => value ? moment(value).format('DD/MM/YYYY') : 'Không giới hạn';
const scopeValues = (version, dimension) => [...new Set((version?.clauses || []).flatMap(clause => (clause.scopes || [])
    .filter(scope => scope.dimension === dimension).flatMap(scope => String(scope.compare_value || '').split(',').filter(Boolean))))];
const optionLabels = (values, options) => values.map(value => (options || []).find(option => String(option.value) === String(value))?.label || `Mục #${value}`);

const ServiceScope = ({ version, capabilities, compact = false }) => {
    const groups = optionLabels(scopeValues(version, 'service_group_id'), capabilities.catalogs?.service_groups);
    const services = optionLabels(scopeValues(version, 'service_id'), capabilities.catalogs?.services);
    const hasAllServices = (version?.clauses || []).some(clause => !(clause.scopes || []).some(scope => ['service_group_id', 'service_id'].includes(scope.dimension)));
    const labels = [...(hasAllServices ? ['Tất cả dịch vụ'] : []), ...groups.map(label => `Nhóm: ${label}`), ...services];
    if (!labels.length) return <Typography.Text type="secondary">Chưa chọn phạm vi dịch vụ</Typography.Text>;
    const visibleLabels = compact ? labels.slice(0, 3) : labels;
    return <Space size={[4, 4]} wrap>{visibleLabels.map(label => <Tag key={label} color="blue">{label}</Tag>)}{compact && labels.length > 3 && <Tag>+{labels.length - 3} phạm vi</Tag>}</Space>;
};

const PolicyDetailDrawer = ({ visible, policy, loading, capabilities, onClose, onCompare }) => <Drawer visible={visible} width={isMobile ? '100%' : "70%"} title={policy?.policy_name || 'Chi tiết chính sách'} maskClosable={false} onClose={onClose}>
    {loading ? <Typography.Text>Đang tải thông tin...</Typography.Text> : policy ? <>
        <Descriptions
            bordered
            size="small"
            column={1}
            style={{ marginBottom: 16 }}
        >
            <Descriptions.Item label="Đối tượng áp dụng">
                {policy.beneficiary_type === 'EMPLOYEE'
                    ? (capabilities.catalogs?.employees || []).find(
                        item => String(item.value) === String(policy.beneficiary_id)
                    )?.label
                    : (capabilities.catalogs?.role_keys || []).find(
                        item => item.value === beneficiaryRole(policy)
                    )?.label}
            </Descriptions.Item>
            <Descriptions.Item label="Vai trò hưởng">{roleLabel(policy, capabilities)}</Descriptions.Item>
            <Descriptions.Item label="Ghi chú">
                {policy.notes || 'Không có'}
            </Descriptions.Item>
        </Descriptions>
        <Tabs defaultActiveKey="versions">
            <Tabs.TabPane tab="Các phiên bản" key="versions">
                {(policy.versions || []).map((version, index) => <Card key={version.id} size="small" style={{ marginBottom: 12 }} title={`Phiên bản ${version.version}`} extra={<Space>{index < policy.versions.length - 1 && <Button icon={<SwapOutlined />} onClick={() => onCompare(version, policy.versions[index + 1])}>So sánh bản trước</Button>}</Space>}><PolicyVersionContent version={version} capabilities={capabilities} /></Card>)}
            </Tabs.TabPane>
            <Tabs.TabPane tab="Lịch sử thao tác" key="audit">
                <PolicyAuditHistory events={policy.events} capabilities={capabilities} />
            </Tabs.TabPane>
        </Tabs>
    </> : null}
</Drawer>;

// Form store thuộc một lần mở và một Policy Version, không thuộc vòng đời của trang.
const PolicyForm = props => props.visible ? <PolicyFormContent key={`${props.current?.id || 'new'}:${latestVersion(props.current || {}).id || 'new'}`} {...props} /> : null;

const PolicyFormContent = ({ visible, current, capabilities, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const { saving, save } = usePolicyFormSave(onSubmit, onCancel);
    const catalogs = capabilities.catalogs || {};
    const conditionOptions = useMemo(
        () => (capabilities.condition_fields || []).map(item => ({
            ...item,
            value: item.value,
            label: item.label,
        })),
        [capabilities.condition_fields]
    );
    const groupedConditionOptions = useMemo(() => {
        const groups = new Map();

        conditionOptions.forEach(item => {
            const groupLabel = item.group_label || 'Khác';

            if (!groups.has(groupLabel)) {
                groups.set(groupLabel, []);
            }

            groups.get(groupLabel).push(item);
        });

        return Array.from(groups.entries()).map(([label, options]) => ({
            label,
            options,
        }));
    }, [conditionOptions]);

    const requirementOptions = conditionOptions.filter(item => String(item.value || '').startsWith('conditions.') && String(item.value || '').endsWith('.is_satisfied'));
    const conditionCatalog = capabilities.condition_fields || [];
    const readConditionValue = condition => condition.value_number ?? condition.value_date ?? condition.value_text
        ?? (Array.isArray(condition.value_json) ? condition.value_json : undefined);
    const version = latestVersion(current || {});
    const hasPublishedVersion = (current?.versions || []).some(item => ['SCHEDULED', 'ACTIVE', 'RETIRED'].includes(item.status));

    const [initialValues] = useState(() => {
        const clauses = (version.clauses || []).map(clause => {
            const serviceGroups = (clause.scopes || []).filter(scope => scope.dimension === 'service_group_id')
                .flatMap(scope => String(scope.compare_value || '').split(','));
            const services = (clause.scopes || []).filter(scope => scope.dimension === 'service_id')
                .flatMap(scope => String(scope.compare_value || '').split(','));
            const components = (clause.scopes || []).filter(scope => scope.dimension === 'component_key')
                .flatMap(scope => String(scope.compare_value || '').split(','));
            const requirements = (clause.conditions || []).filter(item => String(item.field_key).startsWith('conditions.'))
                .filter(item => String(item.field_key).endsWith('.is_satisfied') && item.operator === 'IS_TRUE')
                .map(item => item.field_key);
            const businessConditions = (clause.conditions || []).filter(item => !requirements.includes(item.field_key)).map(item => {
                const catalog = conditionCatalog.find(option => option.value === item.field_key) || {};
                const value = readConditionValue(item);
                return {
                    field_key: item.field_key, operator: item.operator,
                    value: ['DATE', 'DATETIME'].includes(catalog.value_type) && value ? moment(value) : value
                };
            });
            const calculation = (clause.actions || []).find(item => String(item.action_type).startsWith('CALCULATE_')) || {};
            const hold = (clause.actions || []).find(item => item.action_type === 'HOLD_ALLOCATED_REVENUE_PERCENT' || String(item.action_type).startsWith('HOLD_COMMISSION_'));
            return {
                source_clause: clause,
                name: clause.rule_name, service_group_ids: serviceGroups, service_ids: services, component_keys: components,
                requirements, business_conditions: businessConditions,
                condition_match: (clause.conditions || [])[0]?.group_logic === 'OR' ? 'ANY' : 'ALL',
                calculation_stage: clause.rule_level === 'AGGREGATE' ? 'PER_PERIOD' : 'PER_SERVICE',
                formula_type: calculation.action_type || 'CALCULATE_PERCENTAGE',
                rate: calculation.value_json?.rate ?? calculation.value_number,
                base_source: calculation.value_json?.base_source || 'ALLOCATED_REVENUE',
                unit_amount: calculation.value_json?.unit_amount,
                threshold: calculation.value_json?.tiers?.[0]?.to,
                lower_rate: calculation.value_json?.tiers?.[0]?.rate,
                upper_rate: calculation.value_json?.tiers?.[1]?.rate,
                aggregate_mode: calculation.value_json?.cycle_windows?.length ? 'CYCLE' : 'MONTH',
                cycle_windows: (calculation.value_json?.cycle_windows || []).map(window => ({
                    ...window,
                    start: moment(`2000-${window.start}`),
                    end: moment(`2000-${window.end}`),
                })),
                milestones: calculation.value_json?.condition_steps || [],
                payment_dependency: calculation.value_json?.payment_dependency || 'NON_PAYMENT',
                hold_kind: hold?.action_type === 'HOLD_ALLOCATED_REVENUE_PERCENT' ? 'REVENUE' : (hold ? 'COMMISSION' : 'NONE'),
                hold_percent: hold?.value_number,
                hold_months: hold?.value_json?.hold_months,
                release_outcome: hold?.value_json?.release_outcome || 'NO_ISSUE',
                release_condition_code: hold?.value_json?.release_condition_code,
            };
        });
        return {
            policy_name: current?.policy_name,
            beneficiary_type: current?.beneficiary_type || 'EMPLOYEE', beneficiary_id: current?.beneficiary_id ? String(current.beneficiary_id) : undefined,
            role_key: version.role_key || current?.role_key, effective_from: version.effective_from ? moment(version.effective_from) : moment(),
            effective_to: version.effective_to ? moment(version.effective_to) : null, notes: current?.notes,
            change_reason: version.change_reason,
            clauses: clauses.length ? clauses : [{ condition_match: 'ALL', calculation_stage: 'PER_SERVICE', aggregate_mode: 'MONTH', formula_type: 'CALCULATE_PERCENTAGE', base_source: 'ALLOCATED_REVENUE', hold_kind: 'NONE' }],
        };
    });

    const submit = values => {
        const clauseKeys = new Set(values.clauses.map(item => item.source_clause?.rule_code?.split('__').pop()).filter(Boolean));
        const clauses = values.clauses.map((item, index) => {
            let clauseKey = item.source_clause?.rule_code?.split('__').pop();
            if (!clauseKey) {
                // Xóa dòng làm đổi index; mã mới không được trùng trường hợp còn giữ lại.
                let sequence = index + 1;
                while (clauseKeys.has(`CASE_${sequence}`)) sequence += 1;
                clauseKey = `CASE_${sequence}`;
                clauseKeys.add(clauseKey);
            }
            // Giữ nguyên các giới hạn/điều kiện nâng cao của phiên bản đã công bố; form nghiệp vụ chỉ thay phần đang hiển thị.
            const retainedScopes = (item.source_clause?.scopes || []).filter(scope => !['service_group_id', 'service_id', 'component_key', 'employee_id', 'role_key'].includes(scope.dimension));
            const scopes = item.calculation_stage === 'PER_PERIOD' ? retainedScopes.filter(scope => ['branch_id'].includes(scope.dimension))
                : [...retainedScopes,
                ...(item.service_group_ids?.length ? [{ dimension: 'service_group_id', operator: 'IN', compare_value: item.service_group_ids }] : []),
                ...(item.service_ids?.length ? [{ dimension: 'service_id', operator: 'IN', compare_value: item.service_ids }] : []),
                ...(item.component_keys?.length ? [{ dimension: 'component_key', operator: 'IN', compare_value: item.component_keys }] : [])];
            const businessConditions = (item.business_conditions || []).map((condition, conditionIndex) => {
                const catalog = conditionCatalog.find(option => option.value === condition.field_key) || {};
                const payload = {
                    group_no: 1, group_logic: item.condition_match === 'ANY' ? 'OR' : 'AND', sort_order: conditionIndex,
                    field_key: condition.field_key, operator: condition.operator, value_type: catalog.value_type || 'TEXT'
                };
                if (['IS_TRUE', 'IS_FALSE', 'IS_NULL', 'IS_NOT_NULL'].includes(condition.operator)) return payload;
                if (catalog.value_type === 'NUMBER') payload.value_number = Number(condition.value);
                else if (['DATE', 'DATETIME'].includes(catalog.value_type)) payload.value_date = moment.isMoment(condition.value) ? condition.value.format('YYYY-MM-DD HH:mm:ss') : condition.value;
                else if (['IN', 'NOT_IN', 'BETWEEN'].includes(condition.operator)) payload.value_json = condition.value || [];
                else payload.value_text = condition.value;
                return payload;
            });
            const conditions = item.calculation_stage === 'PER_PERIOD' ? businessConditions.filter(condition => String(condition.field_key).startsWith('aggregate.'))
                : [...businessConditions, ...(item.requirements || []).map((fieldKey, conditionIndex) => ({ group_no: 1, group_logic: item.condition_match === 'ANY' ? 'OR' : 'AND', sort_order: businessConditions.length + conditionIndex, field_key: fieldKey, operator: 'IS_TRUE', value_type: 'BOOLEAN' }))];
            const actions = (item.source_clause?.actions || []).filter(action => !String(action.action_type).startsWith('CALCULATE_') && action.action_type !== 'HOLD_ALLOCATED_REVENUE_PERCENT' && !String(action.action_type).startsWith('HOLD_COMMISSION_'));
            const cycleConfig = item.calculation_stage === 'PER_PERIOD' && item.aggregate_mode === 'CYCLE'
                ? { cycle_windows: (item.cycle_windows || []).map(window => ({ key: String(window.key || '').toUpperCase(), start: window.start.format('MM-DD'), end: window.end.format('MM-DD') })) }
                : {};
            if (item.formula_type === 'CALCULATE_PERCENTAGE') actions.push({ group_no: 1, action_type: item.formula_type, value_type: 'JSON', value_json: { base_source: item.base_source, rate: Number(item.rate), ...cycleConfig } });
            if (item.formula_type === 'CALCULATE_FIXED') actions.push({ group_no: 1, action_type: item.formula_type, value_type: 'NUMBER', value_number: Number(item.rate), value_json: cycleConfig });
            if (item.formula_type === 'CALCULATE_UNIT') actions.push({ group_no: 1, action_type: item.formula_type, value_type: 'JSON', value_json: { quantity_source: 'SERVICE_QUANTITY', unit_amount: Number(item.unit_amount) } });
            if (item.formula_type === 'CALCULATE_PROGRESSIVE') actions.push({ group_no: 1, action_type: item.formula_type, value_type: 'JSON', value_json: { base_source: 'ALLOCATED_REVENUE', tiers: [{ from: 0, to: Number(item.threshold), rate: Number(item.lower_rate) }, { from: Number(item.threshold), to: null, rate: Number(item.upper_rate) }], ...cycleConfig } });
            if (item.formula_type === 'CALCULATE_MILESTONE') actions.push({ group_no: 1, action_type: item.formula_type, value_type: 'JSON', value_json: { base_source: item.base_source || 'SERVICE_AMOUNT', payment_dependency: item.payment_dependency || 'NON_PAYMENT', condition_steps: (item.milestones || []).map((step, stepIndex) => ({ key: step.key || `MILESTONE_${stepIndex + 1}`, name: step.name, type: step.type || 'EVENT', event_code: step.type === 'RELATIVE_TIME' ? undefined : step.event_code, anchor_key: step.type === 'RELATIVE_TIME' ? step.anchor_key : undefined, offset_value: step.type === 'RELATIVE_TIME' ? Number(step.offset_value) : undefined, offset_unit: step.type === 'RELATIVE_TIME' ? (step.offset_unit || 'DAY') : undefined, base_share_percent: Number(step.base_share_percent), commission_rate_percent: Number(step.commission_rate_percent) })) } });
            if (item.hold_kind === 'REVENUE') actions.push({ group_no: 1, action_type: 'HOLD_ALLOCATED_REVENUE_PERCENT', value_type: 'JSON', value_number: Number(item.hold_percent), value_json: { hold_months: Number(item.hold_months || 0), release_outcome: item.release_outcome || 'NO_ISSUE' } });
            if (item.hold_kind === 'COMMISSION') actions.push({
                group_no: 1, action_type: 'HOLD_COMMISSION_PERCENT', value_type: 'JSON', value_number: Number(item.hold_percent), value_json: item.release_outcome === 'SERVICE_CONDITION'
                    ? { hold_months: 0, release_outcome: 'SERVICE_CONDITION', release_condition_code: item.release_condition_code }
                    : { hold_months: Number(item.hold_months || 0), release_outcome: item.release_outcome || 'NO_ISSUE' }
            });
            return { clause_key: clauseKey, name: item.name, rule_level: item.calculation_stage === 'PER_PERIOD' ? 'AGGREGATE' : 'SUBJECT', item_type: item.source_clause?.item_type || 'SUBJECT_COMMISSION', priority: item.source_clause?.priority || 100, scopes, conditions, actions };
        });
        return save({
            policy_name: values.policy_name, beneficiary_type: values.beneficiary_type,
            beneficiary_id: values.beneficiary_type === 'EMPLOYEE' ? Number(values.beneficiary_id) : null,
            role_key: values.beneficiary_type !== 'SERVICE' ? values.role_key : null,
            notes: values.notes, change_reason: values.change_reason,
            effective_from: values.effective_from.format('YYYY-MM-DD'),
            effective_to: values.effective_to?.format('YYYY-MM-DD') || null, clauses,
        });
    };

    return <Drawer
        visible={visible}
        width={isMobile ? '100%' : "85%"}
        title={current ? 'Cập nhật chính sách hoa hồng' : 'Tạo chính sách hoa hồng'}
        onClose={onCancel}
        onOk={() => form.submit()}
        destroyOnClose
        maskClosable={false}
        className="commission-policy-form-drawer"
        footer={
            <div className="commission-policy-form-footer">
                <Space>
                    <Button size="large" key="back" onClick={() => onCancel()}>
                        Huỷ
                    </Button>
                    <Button
                        key="submit"
                        type="primary"
                        size="large"
                        loading={saving}
                        onClick={() => form.submit()}
                    >
                        {current ? 'Cập nhật' : 'Lưu bản nháp'}
                    </Button>
                </Space>
            </div>
        }
    >
        <Form
            form={form}
            initialValues={initialValues}
            layout="vertical"
            onFinish={submit}
            className="commission-policy-form"
        >
            <Card
                size="small"
                title="1. Chính sách dành cho ai?"
                className="commission-policy-section-card"
            >
                <Row gutter={[16, 0]}>
                    <Col span={24}>
                        <Form.Item
                            name="policy_name"
                            label="Tên chính sách"
                            rules={[required('tên chính sách')]}
                        >
                            <Input placeholder="Ví dụ: Hoa hồng Implant của bác sĩ phụ trách" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <Form.Item
                            name="beneficiary_type"
                            label="Áp dụng cho"
                            rules={[required('đối tượng áp dụng')]}
                        >
                            <Select disabled={hasPublishedVersion} options={(capabilities.beneficiary_types || []).filter(item => item.value !== 'SERVICE')} />
                        </Form.Item>
                    </Col>

                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => getFieldValue('beneficiary_type') === 'EMPLOYEE'
                            ? <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                                <Form.Item
                                    name="beneficiary_id"
                                    preserve={false}
                                    label="Nhân viên"
                                    rules={[required('nhân viên')]}
                                >
                                    <Select
                                        placeholder="Chọn nhân viên"
                                        disabled={hasPublishedVersion}
                                        showSearch
                                        optionFilterProp="label"
                                        options={catalogs.employees || []}
                                    />
                                </Form.Item>
                            </Col>
                            : null}
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => ['EMPLOYEE', 'ROLE'].includes(getFieldValue('beneficiary_type'))
                            ? <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                                <Form.Item
                                    name="role_key"
                                    preserve={false}
                                    label="Vai trò hưởng hoa hồng"
                                    extra="Chọn vai trò tham gia dịch vụ, không phải chức danh tài khoản. Mỗi vai trò có chính sách riêng."
                                    rules={[required('vai trò')]}
                                >
                                    <Select
                                        placeholder="Chọn vai trò"
                                        options={catalogs.role_keys || []}
                                    />
                                </Form.Item>
                            </Col>
                            : null}
                    </Form.Item>
                </Row>
            </Card>

            <Card
                size="small"
                title="2. Thời gian áp dụng"
                className="commission-policy-section-card"
            >
                <Row gutter={[16, 0]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="effective_from"
                            label="Áp dụng từ ngày"
                            rules={[required('ngày bắt đầu')]}
                        >
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="effective_to"
                            label="Ngừng áp dụng từ ngày (nếu có)"
                        >
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            <Card
                size="small"
                title="3. Các trường hợp được hưởng"
                className="commission-policy-section-card commission-policy-section-card--cases"
            >
                <Form.List name="clauses">
                    {(fields, { add, remove }) => (
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            {fields.map((field, index) => (
                                <Card
                                    key={field.key}
                                    size="small"
                                    title={`Trường hợp ${index + 1}`}
                                    extra={fields.length > 1 && (
                                        <Button
                                            danger
                                            type="text"
                                            onClick={() => remove(field.name)}
                                        >
                                            Xóa
                                        </Button>
                                    )}
                                    className="commission-policy-clause-card"
                                >
                                    <div className="commission-policy-subsection commission-policy-subsection--scope">
                                        <div className="commission-policy-subsection__header">
                                            <div>
                                                <Typography.Text strong>Phạm vi áp dụng</Typography.Text>
                                                <div className="commission-policy-subsection__description">
                                                    Xác định dịch vụ, phần việc và xác nhận chuyên môn thuộc trường hợp này.
                                                </div>
                                            </div>
                                        </div>

                                        <Row gutter={[12, 0]} className="commission-policy-grid">
                                            <Col xs={24} md={12} lg={8}>
                                                <Form.Item
                                                    name={[field.name, 'name']}
                                                    label="Tên trường hợp"
                                                    rules={[required('tên trường hợp')]}
                                                >
                                                    <Input placeholder="Ví dụ: Niềng và Implant" />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} md={12} lg={8}>
                                                <Form.Item
                                                    name={[field.name, 'service_group_ids']}
                                                    label="Nhóm dịch vụ áp dụng"
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        options={catalogs.service_groups || []}
                                                        placeholder="Để trống nếu áp dụng mọi dịch vụ"
                                                    />
                                                </Form.Item>
                                            </Col>

                                            {/* <Col xs={24} md={12} lg={8}>
                                                <Form.Item
                                                    name={[field.name, 'service_ids']}
                                                    label="Dịch vụ cụ thể"
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        showSearch
                                                        optionFilterProp="label"
                                                        options={catalogs.services || []}
                                                        placeholder="Chỉ chọn khi áp dụng cho dịch vụ cụ thể"
                                                    />
                                                </Form.Item>
                                            </Col> */}

                                            <Col xs={24} md={12} lg={8}>
                                                <Form.Item
                                                    name={[field.name, 'component_keys']}
                                                    label="Phần việc được hưởng"
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        options={catalogs.component_keys || []}
                                                        placeholder="Để trống nếu áp dụng toàn bộ phần việc"
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} md={12} lg={16}>
                                                <Form.Item
                                                    name={[field.name, 'requirements']}
                                                    label="Xác nhận chuyên môn bắt buộc"
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        options={requirementOptions}
                                                        placeholder="Dữ liệu được ghi nhận tại dịch vụ chốt"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="commission-policy-subsection commission-policy-subsection--condition">
                                        <div className="commission-policy-subsection__header">
                                            <div>
                                                <Typography.Text strong>Điều kiện áp dụng</Typography.Text>
                                                <div className="commission-policy-subsection__description">
                                                    Thiết lập các điều kiện riêng mà người hưởng cần đáp ứng.
                                                </div>
                                            </div>
                                        </div>

                                        {/* Cách kết hợp thuộc clause, không được đăng ký thành một dòng điều kiện. */}
                                        <div className="commission-policy-condition-toolbar">
                                            <Form.Item
                                                name={[field.name, 'condition_match']}
                                                label="Chính sách áp dụng khi"
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select
                                                    className="commission-policy-condition-match"
                                                    placeholder="Chọn cách xét điều kiện"
                                                    options={[
                                                        {
                                                            value: 'ALL',
                                                            label: 'Đồng thời đạt tất cả điều kiện',
                                                        },
                                                        {
                                                            value: 'ANY',
                                                            label: 'Đạt ít nhất một điều kiện',
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>
                                        </div>

                                        <Form.List name={[field.name, 'business_conditions']}>
                                            {(conditions, { add: addCondition, remove: removeCondition }) => (
                                                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                                                    {conditions.map((condition, conditionIndex) => (
                                                        <Card
                                                            key={condition.key}
                                                            size="small"
                                                            title={`Điều kiện ${conditionIndex + 1}`}
                                                            extra={
                                                                <Button
                                                                    danger
                                                                    type="text"
                                                                    size="small"
                                                                    onClick={() => removeCondition(condition.name)}
                                                                >
                                                                    Xóa
                                                                </Button>
                                                            }
                                                            className="commission-policy-condition-card"
                                                        >
                                                            <Form.Item noStyle shouldUpdate>
                                                                {({ getFieldValue }) => {
                                                                    const path = ['clauses', field.name, 'business_conditions', condition.name];
                                                                    const fieldKey = getFieldValue([...path, 'field_key']);
                                                                    const operator = getFieldValue([...path, 'operator']);
                                                                    const catalog = conditionCatalog.find(item => item.value === fieldKey) || {};
                                                                    const options = (capabilities.condition_operators || []).filter(item =>
                                                                        !item.supported_value_types?.length ||
                                                                        item.supported_value_types.includes(catalog.value_type)
                                                                    );
                                                                    const hideValue = ['IS_TRUE', 'IS_FALSE', 'IS_NULL', 'IS_NOT_NULL'].includes(operator);
                                                                    const optionSource = catalogs[catalog.option_source] || [];

                                                                    let control = <Input placeholder="Nhập giá trị nghiệp vụ" />;
                                                                    if (catalog.input_type === 'number') control = <InputNumber style={{ width: '100%' }} />;
                                                                    if (catalog.input_type === 'date') control = <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />;
                                                                    if (catalog.input_type === 'datetime') control = <DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" />;
                                                                    if (catalog.input_type === 'select') control = <Select showSearch optionFilterProp="label" mode={['IN', 'NOT_IN'].includes(operator) ? 'multiple' : undefined} options={optionSource} />;
                                                                    if (operator === 'BETWEEN') control = <Select mode="tags" tokenSeparators={[',']} placeholder="Nhập hai mốc, ví dụ 0, 10000000" />;

                                                                    return (
                                                                        <Row gutter={[12, 0]} className="commission-policy-grid">
                                                                            <Col xs={24} md={hideValue ? 12 : 8}>
                                                                                <Form.Item
                                                                                    name={[condition.name, 'field_key']}
                                                                                    label="Thông tin cần kiểm tra"
                                                                                    rules={[required('tiêu chí')]}
                                                                                >
                                                                                    <Select
                                                                                        showSearch
                                                                                        optionFilterProp="label"
                                                                                        placeholder="Chọn tiêu chí"
                                                                                        options={groupedConditionOptions}
                                                                                    />
                                                                                </Form.Item>
                                                                            </Col>

                                                                            <Col xs={24} md={hideValue ? 12 : 8}>
                                                                                <Form.Item
                                                                                    name={[condition.name, 'operator']}
                                                                                    label="Điều kiện đạt khi"
                                                                                    rules={[required('cách xét')]}
                                                                                >
                                                                                    <Select options={options} placeholder="Chọn cách xét" />
                                                                                </Form.Item>
                                                                            </Col>

                                                                            {!hideValue && (
                                                                                <Col xs={24} md={8}>
                                                                                    <Form.Item
                                                                                        name={[condition.name, 'value']}
                                                                                        label="Giá trị nghiệp vụ"
                                                                                        rules={[required('giá trị')]}
                                                                                    >
                                                                                        {control}
                                                                                    </Form.Item>
                                                                                </Col>
                                                                            )}
                                                                        </Row>
                                                                    );
                                                                }}
                                                            </Form.Item>
                                                        </Card>
                                                    ))}

                                                    <div className="commission-policy-condition-actions mt-2">
                                                        <Button
                                                            type="dashed"
                                                            size="small"
                                                            onClick={() => addCondition({})}
                                                        >
                                                            Thêm điều kiện riêng
                                                        </Button>
                                                        <Typography.Text type="secondary">
                                                            Hệ thống tự tạo cấu trúc Rule kỹ thuật từ các lựa chọn trên.
                                                        </Typography.Text>
                                                    </div>
                                                </Space>
                                            )}
                                        </Form.List>
                                    </div>

                                    <div className="commission-policy-subsection commission-policy-subsection--calculation">
                                        <div className="commission-policy-subsection__header">
                                            <div>
                                                <Typography.Text strong>Cách tính hoa hồng</Typography.Text>
                                                <div className="commission-policy-subsection__description">
                                                    Xác định cách cộng kết quả, công thức và nguồn doanh thu dùng để tính.
                                                </div>
                                            </div>
                                        </div>

                                        <Form.Item noStyle shouldUpdate>
                                            {({ getFieldValue }) => {
                                                const formulaType = getFieldValue(['clauses', field.name, 'formula_type']);
                                                const calculationStage = getFieldValue(['clauses', field.name, 'calculation_stage']);
                                                const aggregateMode = getFieldValue(['clauses', field.name, 'aggregate_mode']);
                                                const isSalesRole = getFieldValue('role_key') === 'sales';

                                                return (
                                                    <Row gutter={[12, 0]} className="commission-policy-grid">
                                                        <Col xs={24} md={12} lg={8}>
                                                            <Form.Item
                                                                name={[field.name, 'calculation_stage']}
                                                                label="Cách cộng kết quả"
                                                                extra="Theo kỳ dùng cho chính sách lũy tiến tổng doanh thu."
                                                                rules={[{
                                                                    validator: (_, value) => value !== 'PER_PERIOD' || isSalesRole
                                                                        ? Promise.resolve()
                                                                        : Promise.reject(new Error('Thưởng lũy tiến kỳ chỉ áp dụng cho vai trò Sales.')),
                                                                }]}
                                                            >
                                                                <Select
                                                                    options={[
                                                                        { value: 'PER_SERVICE', label: 'Tính trên từng dịch vụ phát sinh' },
                                                                        ...(isSalesRole ? [{ value: 'PER_PERIOD', label: 'Tổng hợp rồi tính theo kỳ' }] : []),
                                                                    ]}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        {calculationStage === 'PER_PERIOD' && (
                                                            <Col xs={24}>
                                                                <Form.Item name={[field.name, 'aggregate_mode']} label="Chu kỳ cộng kết quả">
                                                                    <Select options={[
                                                                        { value: 'MONTH', label: 'Theo từng tháng' },
                                                                        { value: 'CYCLE', label: 'Theo các khoảng cố định trong năm' },
                                                                    ]} />
                                                                </Form.Item>
                                                            </Col>
                                                        )}

                                                        {calculationStage === 'PER_PERIOD' && aggregateMode === 'CYCLE' && (
                                                            <Col xs={24}>
                                                                <Form.List name={[field.name, 'cycle_windows']}>
                                                                    {(windows, { add, remove }) => <Space direction="vertical" style={{ width: '100%' }}>
                                                                        {windows.map((window, windowIndex) => <Card key={window.key} size="small" title={`Khoảng ${windowIndex + 1}`} extra={<Button danger type="text" onClick={() => remove(window.name)}>Xóa</Button>}>
                                                                            <Row gutter={[12, 0]}>
                                                                                <Col xs={24} md={8}><Form.Item name={[window.name, 'key']} label="Mã khoảng" rules={[required('mã khoảng')]}><Input placeholder="Ví dụ: H1" /></Form.Item></Col>
                                                                                <Col xs={24} md={8}><Form.Item name={[window.name, 'start']} label="Bắt đầu" rules={[required('ngày bắt đầu')]}><DatePicker picker="date" format="DD/MM" style={{ width: '100%' }} /></Form.Item></Col>
                                                                                <Col xs={24} md={8}><Form.Item name={[window.name, 'end']} label="Kết thúc" rules={[required('ngày kết thúc')]}><DatePicker picker="date" format="DD/MM" style={{ width: '100%' }} /></Form.Item></Col>
                                                                            </Row>
                                                                        </Card>)}
                                                                        <Button type="dashed" onClick={() => add({ key: windows.length ? `CYCLE_${windows.length + 1}` : 'H1', start: moment('2000-01-01'), end: moment('2000-06-30') })}>Thêm khoảng chu kỳ</Button>
                                                                    </Space>}
                                                                </Form.List>
                                                            </Col>
                                                        )}

                                                        <Col xs={24} md={12} lg={8}>
                                                            <Form.Item
                                                                name={[field.name, 'formula_type']}
                                                                label="Cách tính"
                                                                rules={[required('cách tính')]}
                                                            >
                                                                <Select
                                                                    options={[
                                                                        { value: 'CALCULATE_PERCENTAGE', label: 'Theo tỷ lệ doanh thu' },
                                                                        { value: 'CALCULATE_FIXED', label: 'Số tiền cố định' },
                                                                        { value: 'CALCULATE_UNIT', label: 'Theo số răng/trụ/đơn vị' },
                                                                        { value: 'CALCULATE_PROGRESSIVE', label: 'Lũy tiến theo tổng kỳ' },
                                                                        { value: 'CALCULATE_MILESTONE', label: 'Theo các mốc dịch vụ' },
                                                                    ]}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        <Col xs={24} md={24} lg={8}>
                                                            <Form.Item
                                                                name={[field.name, 'base_source']}
                                                                label="Doanh thu dùng để tính"
                                                            >
                                                                <Select
                                                                    options={[
                                                                        { value: 'ALLOCATED_REVENUE', label: 'Doanh thu đã phân bổ cho người hưởng' },
                                                                        { value: 'SERVICE_AMOUNT', label: 'Giá trị dịch vụ đủ điều kiện' },
                                                                    ]}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        {formulaType === 'CALCULATE_UNIT' && (
                                                            <Col xs={24}>
                                                                <Form.Item
                                                                    name={[field.name, 'unit_amount']}
                                                                    label="Mức tiền mỗi đơn vị"
                                                                    rules={[required('mức tiền')]}
                                                                >
                                                                    <InputNumber min={0} style={{ width: '100%' }} placeholder='Nhập giá trị' />
                                                                </Form.Item>
                                                            </Col>
                                                        )}

                                                        {!['CALCULATE_PROGRESSIVE', 'CALCULATE_MILESTONE', 'CALCULATE_UNIT'].includes(formulaType) && (
                                                            <Col xs={24}>
                                                                <Form.Item
                                                                    name={[field.name, 'rate']}
                                                                    label={formulaType === 'CALCULATE_FIXED' ? 'Số tiền hoa hồng' : 'Tỷ lệ hoa hồng (%)'}
                                                                    rules={[required('mức hoa hồng')]}
                                                                >
                                                                    <InputNumber
                                                                        min={0}
                                                                        placeholder='Nhập giá trị'
                                                                        max={formulaType === 'CALCULATE_PERCENTAGE' ? 100 : undefined}
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                        )}

                                                        {formulaType === 'CALCULATE_PROGRESSIVE' && (
                                                            <>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        name={[field.name, 'threshold']}
                                                                        label="Mốc doanh thu chuyển bậc"
                                                                        rules={[required('mốc doanh thu')]}
                                                                    >
                                                                        <InputNumber min={0} style={{ width: '100%' }} placeholder='Nhập giá trị' />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        name={[field.name, 'lower_rate']}
                                                                        label="Tỷ lệ bậc đầu (%)"
                                                                        rules={[required('tỷ lệ bậc đầu')]}
                                                                    >
                                                                        <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder='Nhập giá trị' />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        name={[field.name, 'upper_rate']}
                                                                        label="Tỷ lệ phần vượt mốc (%)"
                                                                        rules={[required('tỷ lệ phần vượt')]}
                                                                    >
                                                                        <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder='Nhập giá trị' />
                                                                    </Form.Item>
                                                                </Col>
                                                            </>
                                                        )}

                                                        {formulaType === 'CALCULATE_MILESTONE' && (
                                                            <>
                                                                <Col xs={24}>
                                                                    <Form.Item
                                                                        name={[field.name, 'payment_dependency']}
                                                                        label="Điều kiện thanh toán"
                                                                    >
                                                                        <Select
                                                                            placeholder="Chọn điều kiện thanh toán"
                                                                            options={[
                                                                                { value: 'NON_PAYMENT', label: 'Không phụ thuộc thanh toán' },
                                                                                { value: 'PAYMENT_BACKED', label: 'Không vượt phần đã thanh toán' },
                                                                                { value: 'PAID_FULL', label: 'Chỉ khi đã thanh toán đủ' },
                                                                            ]}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>

                                                                <Col span={24}>
                                                                    <Form.List name={[field.name, 'milestones']}>
                                                                        {(steps, { add: addStep, remove: removeStep }) => (
                                                                            <div className="commission-policy-milestones">
                                                                                <div className="commission-policy-milestones__title">
                                                                                    <Typography.Text strong>Các mốc phát sinh hoa hồng</Typography.Text>
                                                                                </div>

                                                                                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                                                                                    {steps.map((step, stepIndex) => (
                                                                                        <Card
                                                                                            key={step.key}
                                                                                            size="small"
                                                                                            title={`Mốc ${stepIndex + 1}`}
                                                                                            extra={
                                                                                                <Button
                                                                                                    danger
                                                                                                    type="text"
                                                                                                    size="small"
                                                                                                    onClick={() => removeStep(step.name)}
                                                                                                >
                                                                                                    Xóa
                                                                                                </Button>
                                                                                            }
                                                                                            className="commission-policy-milestone-card"
                                                                                        >
                                                                                            <Form.Item noStyle shouldUpdate>
                                                                                                {({ getFieldValue: read }) => {
                                                                                                    const stepType = read(['clauses', field.name, 'milestones', step.name, 'type']);
                                                                                                    const isRelative = stepType === 'RELATIVE_TIME';

                                                                                                    return (
                                                                                                        <Row gutter={[12, 0]} className="commission-policy-grid">
                                                                                                            <Col xs={24} md={8}>
                                                                                                                <Form.Item
                                                                                                                    name={[step.name, 'name']}
                                                                                                                    label="Tên mốc"
                                                                                                                    rules={[required('tên mốc')]}
                                                                                                                >
                                                                                                                    <Input placeholder="Ví dụ: Lên mắc cài" />
                                                                                                                </Form.Item>
                                                                                                            </Col>

                                                                                                            <Col xs={24} md={8}>
                                                                                                                <Form.Item
                                                                                                                    name={[step.name, 'type']}
                                                                                                                    label="Mốc xảy ra"
                                                                                                                >
                                                                                                                    <Select
                                                                                                                        options={[
                                                                                                                            { value: 'EVENT', label: 'Theo sự kiện thực tế' },
                                                                                                                            { value: 'RELATIVE_TIME', label: 'Sau một khoảng thời gian' },
                                                                                                                        ]}
                                                                                                                    />
                                                                                                                </Form.Item>
                                                                                                            </Col>

                                                                                                            <Col xs={24} md={8}>
                                                                                                                {isRelative ? (
                                                                                                                    <Form.Item
                                                                                                                        name={[step.name, 'anchor_key']}
                                                                                                                        label="Mốc gốc"
                                                                                                                        rules={[required('mốc gốc')]}
                                                                                                                    >
                                                                                                                        <Select
                                                                                                                            placeholder="Chọn mốc gốc"
                                                                                                                            options={requirementOptions.map(option => ({
                                                                                                                                value: option.value.split('.')[1],
                                                                                                                                label: option.label,
                                                                                                                            }))}
                                                                                                                        />
                                                                                                                    </Form.Item>
                                                                                                                ) : (
                                                                                                                    <Form.Item
                                                                                                                        name={[step.name, 'event_code']}
                                                                                                                        label="Sự kiện thực tế"
                                                                                                                        rules={[required('sự kiện')]}
                                                                                                                    >
                                                                                                                        <Select
                                                                                                                            placeholder="Chọn sự kiện"
                                                                                                                            options={requirementOptions.map(option => ({
                                                                                                                                value: option.value.split('.')[1],
                                                                                                                                label: option.label,
                                                                                                                            }))}
                                                                                                                        />
                                                                                                                    </Form.Item>
                                                                                                                )}
                                                                                                            </Col>

                                                                                                            {isRelative && (
                                                                                                                <>
                                                                                                                    <Col xs={24} md={8}>
                                                                                                                        <Form.Item
                                                                                                                            name={[step.name, 'offset_value']}
                                                                                                                            label="Khoảng cách"
                                                                                                                            rules={[required('khoảng cách')]}
                                                                                                                        >
                                                                                                                            <InputNumber min={0} style={{ width: '100%' }} placeholder='Ví dụ: 90' />
                                                                                                                        </Form.Item>
                                                                                                                    </Col>
                                                                                                                    <Col xs={24} md={8}>
                                                                                                                        <Form.Item
                                                                                                                            name={[step.name, 'offset_unit']}
                                                                                                                            label="Đơn vị"
                                                                                                                        >
                                                                                                                            <Select options={capabilities.milestone_offset_units || []} placeholder='Chọn đơn vị' />
                                                                                                                        </Form.Item>
                                                                                                                    </Col>
                                                                                                                </>
                                                                                                            )}

                                                                                                            <Col xs={24} md={8}>
                                                                                                                <Form.Item
                                                                                                                    name={[step.name, 'base_share_percent']}
                                                                                                                    label="Phần giá trị dịch vụ (%)"
                                                                                                                    rules={[required('phần giá trị')]}
                                                                                                                >
                                                                                                                    <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder='Nhập giá trị' />
                                                                                                                </Form.Item>
                                                                                                            </Col>

                                                                                                            <Col xs={24} md={8}>
                                                                                                                <Form.Item
                                                                                                                    name={[step.name, 'commission_rate_percent']}
                                                                                                                    label="Tỷ lệ hoa hồng (%)"
                                                                                                                    rules={[required('tỷ lệ hoa hồng')]}
                                                                                                                >
                                                                                                                    <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder='Nhập giá trị' />
                                                                                                                </Form.Item>
                                                                                                            </Col>
                                                                                                        </Row>
                                                                                                    );
                                                                                                }}
                                                                                            </Form.Item>
                                                                                        </Card>
                                                                                    ))}

                                                                                    <Button
                                                                                        type="dashed"
                                                                                        size="small"
                                                                                        onClick={() => addStep({ type: 'EVENT', offset_unit: 'DAY' })}
                                                                                    >
                                                                                        Thêm mốc
                                                                                    </Button>
                                                                                </Space>
                                                                            </div>
                                                                        )}
                                                                    </Form.List>
                                                                </Col>
                                                            </>
                                                        )}
                                                    </Row>
                                                );
                                            }}
                                        </Form.Item>
                                    </div>

                                    <div className="commission-policy-subsection commission-policy-subsection--hold">
                                        <div className="commission-policy-subsection__header">
                                            <div>
                                                <Typography.Text strong>Giữ lại và mở khoản giữ</Typography.Text>
                                                <div className="commission-policy-subsection__description">
                                                    Thiết lập phần doanh thu/hoa hồng cần giữ lại và điều kiện được mở giữ.
                                                </div>
                                            </div>
                                        </div>

                                        <Form.Item noStyle shouldUpdate>
                                            {({ getFieldValue }) => {
                                                const holdKind = getFieldValue(['clauses', field.name, 'hold_kind']);
                                                const hasHold = ['REVENUE', 'COMMISSION'].includes(holdKind);
                                                const releaseByCondition = holdKind === 'COMMISSION' && getFieldValue(['clauses', field.name, 'release_outcome']) === 'SERVICE_CONDITION';
                                                const holdActionType = holdKind === 'COMMISSION' ? 'HOLD_COMMISSION_PERCENT' : 'HOLD_ALLOCATED_REVENUE_PERCENT';
                                                const releaseOptions = (capabilities.action_types || []).find(action => action.value === holdActionType)?.fields?.find(item => item.name === 'release_outcome')?.options
                                                    || [{ value: 'NO_ISSUE', label: 'CSKH đã liên hệ, khách không có vấn đề' }];

                                                return (
                                                    <Row gutter={[12, 0]} className="commission-policy-grid">
                                                        <Col xs={24} md={hasHold ? 8 : 24}>
                                                            <Form.Item
                                                                name={[field.name, 'hold_kind']}
                                                                label="Có giữ lại không?"
                                                            >
                                                                <Select
                                                                    options={[
                                                                        { value: 'NONE', label: 'Không giữ' },
                                                                        { value: 'REVENUE', label: 'Giữ một phần doanh thu của người hưởng' },
                                                                        { value: 'COMMISSION', label: 'Giữ một phần hoa hồng đã tính' },
                                                                    ]}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        {hasHold && (
                                                            <>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        name={[field.name, 'hold_percent']}
                                                                        label="Tỷ lệ giữ lại (%)"
                                                                        rules={[required('tỷ lệ giữ')]}
                                                                    >
                                                                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                                                                    </Form.Item>
                                                                </Col>

                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        name={[field.name, 'hold_months']}
                                                                        hidden={releaseByCondition}
                                                                        label="Giữ tối thiểu bao nhiêu tháng?"
                                                                        extra="Tính từ thời điểm khoản bị giữ."
                                                                        rules={releaseByCondition ? [] : [required('thời gian giữ')]}
                                                                    >
                                                                        <InputNumber min={0} max={120} style={{ width: '100%' }} />
                                                                    </Form.Item>
                                                                </Col>

                                                                <Col xs={24}>
                                                                    <Form.Item
                                                                        name={[field.name, 'release_outcome']}
                                                                        label="Điều kiện mở khoản giữ"
                                                                        extra={releaseByCondition ? 'Ghi nhận điều kiện và ngày thực tế tại dịch vụ chốt; hệ thống tự mở giữ.' : 'Ghi nhận tại màn hình Theo dõi khoản giữ; không có nút mở thủ công.'}
                                                                    >
                                                                        <Select
                                                                            options={releaseOptions}
                                                                        />
                                                                    </Form.Item>
                                                                    {releaseByCondition && <Form.Item name={[field.name, 'release_condition_code']} label="Điều kiện dịch vụ mở giữ" rules={[required('điều kiện mở giữ')]}>
                                                                        <Select options={requirementOptions.filter(option => conditionOptions.some(dateField => dateField.value === option.value.replace('.is_satisfied', '.occurred_at'))).map(option => ({ value: option.value.split('.')[1], label: option.label }))} />
                                                                    </Form.Item>}
                                                                </Col>
                                                            </>
                                                        )}
                                                    </Row>
                                                );
                                            }}
                                        </Form.Item>
                                    </div>
                                </Card>
                            ))}

                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => add({
                                    condition_match: 'ALL',
                                    calculation_stage: 'PER_SERVICE',
                                    formula_type: 'CALCULATE_PERCENTAGE',
                                    base_source: 'ALLOCATED_REVENUE',
                                    hold_kind: 'NONE',
                                })}
                                className="commission-policy-add-case"
                            >
                                Thêm trường hợp nghiệp vụ
                            </Button>
                        </Space>
                    )}
                </Form.List>
            </Card>

            <Card
                size="small"
                title="4. Ghi chú và lý do thay đổi"
                className="commission-policy-section-card"
            >
                <Row gutter={[16, 0]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="notes"
                            label="Ghi chú cho người quản trị"
                        >
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="change_reason"
                            label="Lý do tạo hoặc thay đổi"
                        >
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
        </Form>
    </Drawer>;
};

export default function CommissionPolicyPage() {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { policies, pagination, capabilities } = useSelector(state => state.commissionPolicyReducer);

    const filters = useMemo(() => {
        const query = qs.parse(location.search.slice(1));

        return cleanObject({
            ...query,
            page: parseInt(query.page, 10) || 1,
            per_page: parseInt(query.per_page, 10) || 30,
        });
    }, [location.search]);

    const [editing, setEditing] = useState(null); const [visible, setVisible] = useState(false); const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null); const [detailVisible, setDetailVisible] = useState(false); const [detailLoading, setDetailLoading] = useState(false);
    const [comparison, setComparison] = useState(null);

    const pushFilters = nextFilters => {
        const query = cleanObject(nextFilters);

        history.push({
            pathname: location.pathname,
            search: qs.stringify(query),
        });
    };

    const changeFilter = (name, value) => {
        const next = {
            ...filters,
            [name]: value || undefined,
            page: 1,
        };

        if (name === 'beneficiary_type') {
            delete next.beneficiary_id;
            delete next.role_key;
        }

        pushFilters(next);
    };

    const changeTable = (page, pageSize) => {
        const nextPageSize = pageSize || filters.per_page || 30;
        pushFilters({
            ...filters,
            page: nextPageSize !== filters.per_page ? 1 : page,
            per_page: nextPageSize,
        });
    };

    const load = useCallback(() => { setLoading(true); return dispatch(fetchCommissionPolicies(filters)).finally(() => setLoading(false)); }, [dispatch, filters]);
    useEffect(() => { load(); }, [load]);
    const openDetail = row => {
        setDetailVisible(true); setDetailLoading(true);
        dispatch(fetchCommissionPolicyDetail(row.id)).then(setDetail).finally(() => setDetailLoading(false));
    };
    const compareVersions = (version, previous) => dispatch(compareCommissionPolicyVersions(detail.id, version.id, previous.id)).then(setComparison);

    const validateAndActivate = (row, version) => dispatch(validateCommissionPolicyVersion(row.id, version.id)).then(result => {
        if (!result.valid) {
            showCommissionBusinessError({
                operation: 'POLICY_ACTIVATION',
                causes: result.errors,
                nextActions: ['Bổ sung hoặc điều chỉnh các nội dung được nêu.', 'Kiểm tra lại cấu hình trước khi kích hoạt.'],
            });
            return null;
        }
        askReason('Kích hoạt chính sách?', 'Nhập lý do phê duyệt và kích hoạt', reason => dispatch(changeCommissionPolicyStatus(row.id, version.id, 'ACTIVE', reason)).then(load));
    });

    const clonePolicy = row => {
        const nameId = `clone-name-${row.id}`;
        const reasonId = `clone-reason-${row.id}`;

        Modal.confirm({
            title: 'Sao chép chính sách',
            width: 560,
            okText: 'Tạo bản sao',
            cancelText: 'Hủy',
            content: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                        id={nameId}
                        defaultValue={`${row.policy_name} - Bản sao`}
                        placeholder="Tên chính sách mới"
                    />
                    <Input.TextArea
                        id={reasonId}
                        rows={2}
                        placeholder="Lý do sao chép (bắt buộc)"
                    />
                </Space>
            ),
            onOk: close => {
                const values = {
                    policy_name: document.getElementById(nameId)?.value?.trim(),
                    change_reason: document.getElementById(reasonId)?.value?.trim(),
                };

                if (!values.policy_name) {
                    showCommissionBusinessError({ operation: 'POLICY', problem: 'Chưa thể tạo bản sao chính sách.', causes: 'Tên chính sách mới chưa được nhập.', nextActions: 'Nhập tên chính sách mới rồi chọn “Tạo bản sao”.' });
                    return;
                }

                if (!values.change_reason) {
                    showCommissionBusinessError({ operation: 'POLICY', problem: 'Chưa thể tạo bản sao chính sách.', causes: 'Lý do sao chép chưa được nhập.', nextActions: 'Nhập lý do để bảo đảm lịch sử thay đổi rồi thử lại.' });
                    return;
                }

                return dispatch(cloneCommissionPolicy(row.id, values))
                    .then(() => {
                        load();
                        close();
                    });
            },
        });
    };

    const askReason = (title, placeholder, action) => {
        const inputId = `policy-reason-${Date.now()}`;

        Modal.confirm({
            title,
            content: (
                <Input.TextArea
                    id={inputId}
                    rows={3}
                    placeholder={`${placeholder} (bắt buộc)`}
                />
            ),
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            onOk: close => {
                const reason = document.getElementById(inputId)?.value?.trim();

                if (!reason) {
                    showCommissionBusinessError({ operation: 'POLICY', problem: 'Chưa thể thực hiện thay đổi trạng thái.', causes: 'Lý do thực hiện chưa được nhập.', nextActions: 'Nhập lý do rõ ràng rồi xác nhận lại.' });
                    return;
                }

                return Promise.resolve(action(reason))
                    .then(() => {
                        close();
                    });
            },
        });
    };

    const columns = [
        {
            title: 'Chính sách',
            dataIndex: 'policy_name',
            render: (value, row) => {
                const beneficiaryLabel =
                    row.beneficiary_type === 'EMPLOYEE'
                        ? (capabilities.catalogs?.employees || []).find(
                            item => String(item.value) === String(row.beneficiary_id)
                        )?.label
                        : row.beneficiary_type === 'ROLE'
                            ? roleLabel(row, capabilities)
                            : 'Điều kiện dịch vụ dùng chung';

                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            minWidth: 220,
                        }}
                    >
                        <Button
                            type="link"
                            style={{
                                padding: 0,
                                height: 'auto',
                                fontWeight: 600,
                                textAlign: 'left',
                                justifyContent: 'flex-start',
                            }}
                            onClick={() => openDetail(row)}
                        >
                            {value}
                        </Button>

                        <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                        >
                            Đối tượng hưởng: {beneficiaryLabel || '—'}
                        </Typography.Text>

                        <Space size={6}>
                            <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                            >
                                Vai trò hưởng:
                            </Typography.Text>

                            <Tag
                                color={beneficiaryRole(row) ? 'blue' : 'orange'}
                                style={{ margin: 0 }}
                            >
                                {roleLabel(row, capabilities)}
                            </Tag>
                        </Space>
                    </div>
                );
            },
        },
        { title: 'Phạm vi dịch vụ', width: 250, render: (_, row) => <ServiceScope version={latestVersion(row)} capabilities={capabilities} compact /> },
        { title: 'Phiên bản', render: (_, row) => `v${latestVersion(row).version || '-'}` },
        { title: 'Thời gian áp dụng', render: (_, row) => <><Typography.Text>{formatDate(latestVersion(row).effective_from)}</Typography.Text><br /><Typography.Text type="secondary">đến {formatDate(latestVersion(row).effective_to)}</Typography.Text></> },
        { title: 'Trạng thái', render: (_, row) => <Tag color={statusColor(latestVersion(row).status)}>{statusLabel(latestVersion(row).status)}</Tag> },
        {
            title: 'Thao tác', width: 300, render: (_, row) => {
                const version = latestVersion(row); return <Space wrap size={8}>
                    {version.status === 'DRAFT' && <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(row); setVisible(true); }}>Sửa</Button>}
                    {version.status === 'DRAFT' && <Button size="small" icon={<CheckCircleOutlined />} onClick={() => validateAndActivate(row, version)}>Kiểm tra và kích hoạt</Button>}
                    {['SCHEDULED', 'ACTIVE', 'RETIRED'].includes(version.status) && <Button size="small" icon={<HistoryOutlined />} onClick={() => askReason('Tạo phiên bản mới?', 'Mô tả nội dung dự kiến thay đổi', reason => dispatch(createCommissionPolicyVersion(row.id, { change_reason: reason })).then(load))}>Tạo phiên bản mới</Button>}
                    {version.status === 'SCHEDULED' && <Button size="small" onClick={() => askReason('Hủy lịch áp dụng?', 'Nhập lý do hủy lịch', reason => dispatch(changeCommissionPolicyStatus(row.id, version.id, 'DRAFT', reason)).then(load))}>Hủy lịch</Button>}
                    {version.status === 'ACTIVE' && <Button danger size="small" icon={<StopOutlined />} onClick={() => askReason('Ngừng áp dụng chính sách?', 'Nhập lý do ngừng áp dụng', reason => dispatch(changeCommissionPolicyStatus(row.id, version.id, 'RETIRED', reason)).then(load))}>Ngừng</Button>}
                    <Button size="small" icon={<CopyOutlined />} onClick={() => clonePolicy(row)}>Sao chép</Button>
                </Space>;
            }
        },
    ];

    const routes = [
        {
            path: '/',
            breadcrumbName: 'Trang chủ',
        },
        {
            path: '/commission-policies',
            breadcrumbName: 'Chính sách hoa hồng',
        },
    ]

    const policyFilters = [
        <Select
            allowClear
            showSearch
            optionFilterProp="label"
            value={filters.beneficiary_type || undefined}
            placeholder="Đối tượng hưởng"
            options={(capabilities.beneficiary_types || []).filter(item => item.value !== 'SERVICE')}
            onChange={value => changeFilter('beneficiary_type', value)}
            className="table-button"
            style={{ minWidth: 250 }}
        />,
        <Select
            allowClear showSearch optionFilterProp="label"
            value={filters.service_group_id ? String(filters.service_group_id) : undefined}
            placeholder="Nhóm dịch vụ"
            options={capabilities.catalogs?.service_groups || []}
            onChange={value => changeFilter('service_group_id', value)}
            className="table-button" style={{ minWidth: 220 }}
        />,
        <Select
            allowClear showSearch optionFilterProp="label"
            value={filters.service_id ? String(filters.service_id) : undefined}
            placeholder="Dịch vụ cụ thể"
            options={capabilities.catalogs?.services || []}
            onChange={value => changeFilter('service_id', value)}
            className="table-button" style={{ minWidth: 250 }}
        />,
        <Select
            allowClear
            showSearch
            optionFilterProp="label"
            value={filters.beneficiary_id ? String(filters.beneficiary_id) : undefined}
            placeholder="Nhân viên"
            options={capabilities.catalogs?.employees || []}
            onChange={value => changeFilter('beneficiary_id', value)}
            disabled={Boolean(filters.beneficiary_type) && filters.beneficiary_type !== 'EMPLOYEE'}
            className="table-button"
            style={{ minWidth: 250 }}
        />
    ];

    return <div><PageTitle routes={routes} title="Chính sách hoa hồng" />
        <TableActionBar
            isShowAddButton={true}
            isShowDeleteButton={false}
            onAdd={() => {
                setEditing(null);
                setVisible(true);
            }}
            showFilter={false}
            onSearch={keyword => changeFilter('keyword', keyword)}
            defaultKeyword={filters.keyword}
        >
            {policyFilters}
            <Button
                block={isMobile}
                className="table-button"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() =>
                    load()
                }
            >
                Làm mới
            </Button>
        </TableActionBar>

        <Alert type="warning" message="Một người hoặc vai trò có thể có nhiều chính sách theo từng nhóm dịch vụ, dịch vụ hoặc phần việc. Chính sách không chọn phạm vi sẽ áp dụng cho tất cả dịch vụ phù hợp." className="mb-3" />

        <Table
            size="small"
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={policies}
            pagination={{
                current: pagination.current_page || filters.page || 1,
                total: pagination.total || 0,
                pageSize: pagination.per_page || filters.per_page || 30,
                showSizeChanger: true,
                pageSizeOptions: ['10', '30', '50', '100'],
                showTotal: total => `Tổng ${total} chính sách`,
                onChange: changeTable,
                onShowSizeChange: changeTable,
            }}
            scroll={{ x: 'max-content' }}
        />
        <PolicyForm visible={visible} current={editing} capabilities={capabilities} onCancel={() => { setVisible(false); setEditing(null); }} onSubmit={values => dispatch(saveCommissionPolicy(editing, values)).then(result => { load(); return result; })} />
        <PolicyDetailDrawer visible={detailVisible} policy={detail} loading={detailLoading} capabilities={capabilities} onClose={() => setDetailVisible(false)} onCompare={compareVersions} />
        <Modal visible={Boolean(comparison)} width={isMobile ? '100%' : "70%"} title={comparison ? `So sánh phiên bản ${comparison.from.version} và ${comparison.to.version}` : 'So sánh phiên bản'} footer={<Button onClick={() => setComparison(null)}>Đóng</Button>} onCancel={() => setComparison(null)}>
            {comparison && (comparison.changes || []).length ? <Table size="small" rowKey={(row, index) => `${row.area}-${index}`} pagination={false} dataSource={comparison.changes} columns={[{ title: 'Nội dung thay đổi', dataIndex: 'area' }, { title: `Phiên bản ${comparison.from.version}`, dataIndex: 'before', render: value => value || 'Không có' }, { title: `Phiên bản ${comparison.to.version}`, dataIndex: 'after', render: value => value || 'Không có' }]} /> : <Empty description="Hai phiên bản không có khác biệt được ghi nhận" />}
        </Modal>
    </div>;
}
